/**
 * Advanced Settings Component
 *
 * Component for managing advanced plugin settings and customizations.
 *
 * @since 1.0.0
 */

import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { FlexItem } from '@redfoxwp/core-components/components/flex-item';
import { LoadingSpinner } from '@redfoxwp/core-components/components/loading-spinner';
import { SettingWrapper } from '@redfoxwp/core-components/components/setting-wrapper';
import { __ } from '@wordpress/i18n';
import { useEffect } from 'react';

import { useSettingsContext } from '../../../contexts/settings-context';
import { useValidationContext } from '../../../contexts/validation-context';
import type { CssValidationError } from '../../../utils/css-validator/types';
import { CssInput } from '../../css-input';

const COMPONENT_KEY = 'advanced-css';

/**
 * Advanced Settings component
 *
 * @since 1.0.0
 *
 * @return JSX element.
 */
export const AdvancedSettings = (): JSX.Element => {
  const { settings, loading, isSaving, isResetting, updateSettings } = useSettingsContext();
  const { registerValidation, unregisterValidation } = useValidationContext();

  // Register/unregister this component with the validation context
  useEffect(() => {
    return () => {
      unregisterValidation(COMPONENT_KEY);
    };
  }, [unregisterValidation]);

  const handleCustomCssChange = (value: string, _isValid: boolean) => {
    // Always update the settings value regardless of validity
    // This allows users to see their changes while typing
    updateSettings('advanced', {
      ...settings?.advanced,
      custom_css: value,
    });
  };

  const handleValidationChange = (errors: CssValidationError[]) => {
    // Register validation state with the global validation context
    registerValidation(COMPONENT_KEY, {
      hasErrors: errors.length > 0,
      errors,
    });
  };

  // Show loading spinner only on initial data load
  if (loading) {
    return (
      <Card
        styleProps={{
          padding: '30px',
        }}
      >
        <LoadingSpinner
          isLoading={true}
          variant='ring'
          size='medium'
          centered={true}
          showText={true}
          text={__('Loading settings...', 'redfox-added-to-cart-popup')}
        />
      </Card>
    );
  }

  // Disable inputs during save/reset operations
  const isDisabled = isSaving || isResetting;

  return (
    <Flex direction='column' gap='20px' fullWidth>
      <FlexItem>
        <SettingWrapper
          label={__('Custom CSS', 'redfox-added-to-cart-popup')}
          description={__(
            'Add custom CSS styles to customize the popup appearance. This CSS will be applied directly to the popup. Invalid CSS will not be saved.',
            'redfox-added-to-cart-popup'
          )}
          styleProps={{
            containerPadding: '30px',
          }}
        >
          <CssInput
            value={settings?.advanced?.custom_css ?? ''}
            onChange={handleCustomCssChange}
            onValidationChange={handleValidationChange}
            height='300px'
            disabled={isDisabled}
            showErrors={true}
          />
        </SettingWrapper>
      </FlexItem>
    </Flex>
  );
};
